import React, { useCallback, useEffect, useState } from "react";
import {
    Users, CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw,
    Globe, Building2, Mail, ArrowRight,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin Recruiter Approvals — LinkedIn-style employee verification queue.
 *
 *  - Recruiter self-signups arrive here with status="pending_approval".
 *  - We show the recruiter's email, their claimed employer website, and a
 *    green/amber badge indicating whether the email domain matches the
 *    website domain (auto-computed on register). When the domain matches
 *    an existing Simonara employer user, that employer is shown too.
 *  - One-click "Approve" flips status → active and, if we found a matching
 *    employer, links employer_id on the recruiter.
 *  - "Reject" sets status="rejected" and drops all sessions.
 */

const Badge = ({ tone, icon: Icon, children }) => {
    const tones = {
        ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
        warn: "bg-amber-50 text-amber-800 border-amber-300",
        muted: "bg-slate-50 text-slate-500 border-slate-200",
    };
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
            {Icon && <Icon className="h-3 w-3" />}
            {children}
        </span>
    );
};

const RecruiterCard = ({ row, onAction, busy }) => {
    const created = row.created_at ? new Date(row.created_at).toLocaleString() : "—";
    const match = row.employer_domain_match;
    const suggested = row.suggested_employer;

    return (
        <div
            data-testid={`rec-approval-row-${row.user_id}`}
            className="rounded-2xl border border-border bg-card p-5 shadow-sm"
        >
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-foreground">{row.name || row.email}</h3>
                        <Badge tone="muted">Pending</Badge>
                        {match ? (
                            <Badge tone="ok" icon={CheckCircle2}>Domain match</Badge>
                        ) : (
                            <Badge tone="warn" icon={AlertTriangle}>Domain mismatch</Badge>
                        )}
                    </div>
                    <div className="mt-2 grid gap-1.5 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5" />
                            <span className="font-medium text-foreground">{row.email}</span>
                            {row.email_domain && (
                                <span className="text-xs text-muted-foreground">
                                    ({row.email_domain})
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Globe className="h-3.5 w-3.5" />
                            <span>
                                {row.employer_website ? (
                                    <a
                                        href={row.employer_website}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        className="underline text-foreground hover:text-emerald-700"
                                    >
                                        {row.employer_website}
                                    </a>
                                ) : (
                                    <em className="text-xs">no website provided</em>
                                )}
                            </span>
                            {row.employer_domain && (
                                <span className="text-xs text-muted-foreground">
                                    ({row.employer_domain})
                                </span>
                            )}
                        </div>
                        {suggested && (
                            <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs dark:border-emerald-500/30 dark:bg-emerald-900/20">
                                <Building2 className="h-3.5 w-3.5 text-emerald-700" />
                                <span className="text-emerald-800 dark:text-emerald-200">
                                    Matches existing employer{" "}
                                    <span className="font-semibold">
                                        {suggested.company_name || suggested.name || suggested.email}
                                    </span>
                                </span>
                                <ArrowRight className="h-3 w-3 text-emerald-600" />
                                <span className="text-emerald-700 dark:text-emerald-300">
                                    will link employer_id
                                </span>
                            </div>
                        )}
                        <div className="text-xs text-muted-foreground">Requested {created}</div>
                    </div>
                </div>

                <div className="flex gap-2 shrink-0">
                    <button
                        onClick={() => onAction(row, "reject")}
                        disabled={busy}
                        data-testid={`rec-approval-reject-${row.user_id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                    >
                        <XCircle className="h-3.5 w-3.5" /> Reject
                    </button>
                    <button
                        onClick={() => onAction(row, "approve")}
                        disabled={busy}
                        data-testid={`rec-approval-approve-${row.user_id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                        <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                    </button>
                </div>
            </div>
        </div>
    );
};

export default function RecruiterApprovals() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/recruiter-approvals/pending");
            setRows(r.data.pending || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleAction = async (row, kind) => {
        const action = kind === "approve" ? "approve_recruiter" : "reject_recruiter";
        const label = kind === "approve" ? "Approve" : "Reject";
        if (kind === "reject" && !window.confirm(`Reject ${row.email}? They will be signed out and marked as rejected.`)) {
            return;
        }
        setBusyId(row.user_id);
        try {
            await api.post(`/admin/users/${row.user_id}/action`, { action });
            toast.success(`${label}d ${row.email}`);
            setRows((prev) => prev.filter((x) => x.user_id !== row.user_id));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div data-testid="admin-recruiter-approvals">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Command Center</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Recruiter approvals
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        LinkedIn-style verification queue. Approve recruiters after
                        confirming they represent the employer they claim.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    data-testid="rec-approval-refresh"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-60"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="mt-6">
                {loading ? (
                    <div className="py-16 flex justify-center">
                        <Loader2 size={18} className="animate-spin text-emerald-500" />
                    </div>
                ) : rows.length === 0 ? (
                    <div
                        data-testid="rec-approval-empty"
                        className="rounded-2xl border border-dashed border-border bg-card/60 p-12 text-center"
                    >
                        <Users className="mx-auto h-8 w-8 text-muted-foreground" />
                        <h3 className="mt-3 text-base font-semibold text-foreground">All clear</h3>
                        <p className="mt-1 text-sm text-muted-foreground">
                            No recruiter accounts are waiting for approval right now.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {rows.map((row) => (
                            <RecruiterCard
                                key={row.user_id}
                                row={row}
                                onAction={handleAction}
                                busy={busyId === row.user_id}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
